import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, Award, FileText, RefreshCw, Play,
  ChevronRight, ClipboardCheck
} from "lucide-react";
import COURSE_QUIZZES from "@/data/courseQuizzes";

/**
 * QuizDialog - Competency assessment modal for course completion
 * Handles quiz questions, answer tracking, and score calculation
 */
export default function QuizDialog({
  isOpen,
  onClose,
  course,
  onComplete,
  onViewCertificate,
  onReviewVideo
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);

  if (!course) return null;

  const quiz = COURSE_QUIZZES[course.id] || COURSE_QUIZZES["default"];
  const question = quiz.questions[currentQuestion];
  const passed = quizScore >= quiz.passingScore;

  const handleAnswerSelect = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setSelectedAnswers({ ...selectedAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let correct = 0;
    quiz.questions.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        correct++;
      }
    });
    const score = Math.round((correct / quiz.questions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
    
    if (score >= quiz.passingScore) {
      onComplete?.(course.id, score);
    }
  };

  const retryQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
    setShowExplanation(false);
  };

  const handleClose = () => {
    retryQuiz();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-primary">
              <ClipboardCheck className="w-3 h-3 mr-1" />
              Competency Assessment
            </Badge>
            <Badge variant="outline">80% Required to Pass</Badge>
          </div>
          <DialogTitle>{course.title}</DialogTitle>
          <DialogDescription>
            Answer the following questions to earn {course.ceuCredits} CEU credits
          </DialogDescription>
        </DialogHeader>

        {!quizSubmitted ? (
          <div className="space-y-6 py-4">
            {/* Progress indicator */}
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>Question {currentQuestion + 1} of {quiz.questions.length}</span>
              <Progress value={((currentQuestion + 1) / quiz.questions.length) * 100} className="w-32 h-2" />
            </div>

            {/* Question */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{question.question}</h3>
              
              <RadioGroup
                value={selectedAnswers[question.id]?.toString()}
                onValueChange={(value) => handleAnswerSelect(question.id, parseInt(value))}
              >
                {question.options.map((option, index) => (
                  <div 
                    key={index}
                    className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedAnswers[question.id] === index 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleAnswerSelect(question.id, index)}
                  >
                    <RadioGroupItem value={index.toString()} id={`q${question.id}-${index}`} />
                    <Label htmlFor={`q${question.id}-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
              >
                Previous
              </Button>
              
              {currentQuestion < quiz.questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentQuestion(currentQuestion + 1)}
                  disabled={selectedAnswers[question.id] === undefined}
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={submitQuiz}
                  disabled={Object.keys(selectedAnswers).length < quiz.questions.length}
                >
                  Submit Quiz
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Score Display */}
            <div className={`text-center p-8 rounded-xl ${passed ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                passed ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {passed ? (
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                ) : (
                  <XCircle className="w-10 h-10 text-red-600" />
                )}
              </div>
              <h3 className={`text-3xl font-bold mb-2 ${passed ? 'text-green-700' : 'text-red-700'}`}>
                {quizScore}%
              </h3>
              <p className={`text-lg ${passed ? 'text-green-600' : 'text-red-600'}`}>
                {passed ? 'Congratulations! You passed!' : 'Not quite. 80% required to pass.'}
              </p>
              {passed && (
                <div className="mt-4 flex items-center justify-center gap-2 text-green-700">
                  <Award className="w-5 h-5" />
                  <span className="font-medium">{course.ceuCredits} CEU Credits Earned!</span>
                </div>
              )}
            </div>

            {/* Answer Review */}
            <div>
              <Button 
                variant="ghost" 
                className="w-full justify-between"
                onClick={() => setShowExplanation(!showExplanation)}
              >
                Review Answers
                <ChevronRight className={`w-4 h-4 transition-transform ${showExplanation ? 'rotate-90' : ''}`} />
              </Button>
              
              {showExplanation && (
                <div className="space-y-4 mt-4">
                  {quiz.questions.map((q, index) => {
                    const isCorrect = selectedAnswers[q.id] === q.correctAnswer;
                    return (
                      <div key={q.id} className={`p-4 rounded-lg border ${isCorrect ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
                        <div className="flex items-start gap-2 mb-2">
                          {isCorrect ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium text-sm">Q{index + 1}: {q.question}</p>
                            <p className="text-sm mt-1">
                              Your answer: <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                                {q.options[selectedAnswers[q.id]]}
                              </span>
                            </p>
                            {!isCorrect && (
                              <p className="text-sm text-green-600 mt-1">
                                Correct: {q.options[q.correctAnswer]}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              {q.explanation}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {passed ? (
                <>
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      handleClose();
                      onViewCertificate?.(course);
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Button className="flex-1" onClick={retryQuiz}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={() => {
                    handleClose();
                    onReviewVideo?.(course);
                  }}>
                    <Play className="w-4 h-4 mr-2" />
                    Review Video
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
